/* eslint-disable no-case-declarations */
/* eslint-disable no-unused-vars */

import React, { useState, useReducer } from 'react';
import { UploadItems } from 'store/types';
import each from 'lodash-es/each';
import reduce from 'lodash-es/reduce';
import omit from 'lodash-es/omit';
import isEmpty from 'lodash-es/isEmpty';

import { getMIMEType, shieldFileName } from 'utils/utils';

// WARNING!
// В объекте UploadItem название файла превращается в id, где символы
// Квадратных скобок заменяются на обычные скобки, поэтому, чтобы получить доступ
// К объекту через имя файла, нужно имя файла сначала пропустить через shieldFileName

const prevent = (e: React.DragEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

type LoadingState = { [x: string]: boolean };
type LoadingAction = { type: 'start' | 'finish'; id: string };

const loadingReducer = (
  state: LoadingState,
  action: LoadingAction
): LoadingState => {
  switch (action.type) {
    case 'start':
      return { ...state, [action.id]: true };
    case 'finish':
      return { ...omit(state, action.id) };
    default:
      return state;
  }
};

const initialState: LoadingState = {};

export const useFileList = () => {
  const [list, setList] = useState<UploadItems>({});
  const [loading, dispatchLoading] = useReducer(loadingReducer, initialState);

  const processFiles = (files: FileList) => {
    each(files, (f) => {
      const id = shieldFileName(f.name);

      dispatchLoading({
        type: 'start',
        id: id,
      });

      const blobReader = new FileReader();
      blobReader.onloadend = function () {
        let dataURL = '';

        if (blobReader.result) {
          let blob = new Blob([blobReader.result]);
          dataURL = URL.createObjectURL(blob);
        }

        setList((old) => {
          return {
            ...old,
            [id]: {
              src: dataURL,
              id,
              originalName: f.name,
              tags: {},
              binary: blobReader.result,
              file: f,
            },
          };
        });

        dispatchLoading({
          type: 'finish',
          id: id,
        });
      };

      if (f) {
        blobReader.readAsArrayBuffer(f);
      } else {
        return;
      }
    });
  };

  const onDrop = (e: React.DragEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const files = e.dataTransfer.files;

    processFiles(files);
  };

  const onBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files !== null) {
      processFiles(files);
    }
  };

  // Возвращает из объекта list все значения по ключам объекта keys
  const getByKeys = (keys: { [x: string]: boolean }) => {
    return reduce(
      keys,
      (acc: UploadItems, val, id) => {
        if (val && list[id]) {
          acc[id] = list[id];
        }

        return acc;
      },
      {}
    );
  };

  const pushItems = (items: UploadItems) => {
    setList((old) => {
      return {
        ...old,
        ...items,
      };
    });
  };

  const removeFile = (id: string) => {
    setList((old) => {
      return {
        ...omit(old, id),
      };
    });
  };

  const addFiles = (files: FileList) => {
    processFiles(files);
  };

  const removeAllFiles = () => {
    setList({});
  };

  const validate = () => {
    if (isEmpty(list)) {
      return 'empty';
    }

    return 'success';
  };

  return {
    list,
    onDrop,
    onBrowse,
    prevent,
    getByKeys,
    addFiles,
    removeFile,
    removeAllFiles,
    validate,
    loading,
    _setList: setList,
    pushItems,
  };
};

export type UseFileListIntrface = ReturnType<typeof useFileList>;
